import React, { PropsWithChildren, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  Link,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import CloudIcon from '@material-ui/icons/Cloud';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import WorkIcon from '@material-ui/icons/Work';
import { useApp } from '@backstage/core-plugin-api';
import { useArtaEdition } from '../../artaEdition';
import { useGovernanceStatus } from '../governance/useGovernanceStatus';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

function getLogo() {
  // Always use the default CIDP logo
  return [<LogoFull />, <LogoIcon />];
}

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  const [fullLogo, iconLogo] = getLogo();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? fullLogo : iconLogo}
      </Link>
    </div>
  );
};

// Backstage hardcodes " Catalog" into the CatalogIndexPage browser tab title
// (e.g. "CUBEANGLE Catalog | ARTA by CUBEANGLE"). This observer fires whenever
// document.title changes and strips the word, giving us
// "CUBEANGLE | ARTA by CUBEANGLE".
const PageTitleFixer = () => {
  useEffect(() => {
    const titleEl = document.querySelector('title');
    if (!titleEl) return undefined;

    const fix = () => {
      if (/ Catalog(\s*\||\s*$)/.test(document.title)) {
        document.title = document.title.replace(' Catalog', '');
      }
      const titleParts = document.title.split(' | ');
      if (titleParts.length === 2 && titleParts[0] === titleParts[1]) {
        document.title = titleParts[0];
      }
    };

    fix();
    const observer = new MutationObserver(fix);
    observer.observe(titleEl, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  return null;
};

const GovernanceSidebarItem = () => {
  const { status } = useGovernanceStatus();
  return status ? (
    <SidebarItem
      icon={VerifiedUserIcon}
      to="governance"
      text={status.isAdmin ? 'Project Entitlements' : 'My Architecture'}
    />
  ) : null;
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const { awsEnabled, azureEnabled, features } = useArtaEdition();

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem icon={HomeIcon} to="catalog" text="Catalog" />
          <SidebarItem icon={WorkIcon} to="services" text="My Service Access" />
          <SidebarDivider />
          {azureEnabled && (
            <SidebarGroup label="Azure" icon={<MenuIcon />}>
              <SidebarItem icon={CloudIcon} text="Azure">
                <SidebarSubmenu title="Architecture Catalog">
                  <SidebarSubmenuItem
                    title="Environments"
                    to="catalog?filters[kind]=azureenvironment"
                    icon={useApp().getSystemIcon('kind:domain')}
                  />
                  <SidebarSubmenuItem
                    title="Providers"
                    to="catalog?filters[kind]=azureenvironmentprovider"
                    icon={useApp().getSystemIcon('kind:system')}
                  />
                  <SidebarSubmenuItem
                    title="Apps"
                    to="catalog?filters[kind]=component&filters[metadata.tags]=azure"
                    icon={useApp().getSystemIcon('kind:component')}
                  />
                  <SidebarSubmenuItem
                    title="Resources"
                    to="catalog?filters[kind]=resource&filters[metadata.tags]=azure"
                    icon={useApp().getSystemIcon('kind:resource')}
                  />
                </SidebarSubmenu>
              </SidebarItem>
            </SidebarGroup>
          )}
          {awsEnabled && (
            <SidebarGroup label="AWS" icon={<MenuIcon />}>
              <SidebarItem icon={CloudIcon} text="AWS">
                <SidebarSubmenu title="Architecture Catalog">
                  <SidebarSubmenuItem
                    title="Environments"
                    to="catalog?filters[kind]=awsenvironment"
                    icon={useApp().getSystemIcon('kind:domain')}
                  />
                  <SidebarSubmenuItem
                    title="Providers"
                    to="catalog?filters[kind]=awsenvironmentprovider"
                    icon={useApp().getSystemIcon('kind:system')}
                  />
                  <SidebarSubmenuItem
                    title="Apps"
                    to="catalog?filters[kind]=component&filters[metadata.tags]=aws"
                    icon={useApp().getSystemIcon('kind:component')}
                  />
                  <SidebarSubmenuItem
                    title="Resources"
                    to="catalog?filters[kind]=resource&filters[metadata.tags]=aws"
                    icon={useApp().getSystemIcon('kind:resource')}
                  />
                </SidebarSubmenu>
              </SidebarItem>
            </SidebarGroup>
          )}
          <SidebarDivider />
          <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
          <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
          <SidebarItem
            icon={CreateComponentIcon}
            to="create"
            text="Create..."
          />
          {features.templateManagement && (
            <SidebarItem
              icon={ViewModuleIcon}
              to="template-management"
              text="Template Management"
            />
          )}
          <GovernanceSidebarItem />
          {/* End global nav */}
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      <PageTitleFixer />
      {children}
    </SidebarPage>
  );
};
